import { isFrontDeviceId, isRearDeviceId } from '../../../helpers/devicePairing';
import type { EventDeviceRow } from '../types';
import { normalizeRaceStatus } from './eventsOverviewHelpers';

export function isEventDeviceRetrieved(
  row: Pick<EventDeviceRow, 'retrieved' | 'recalled'>,
): boolean {
  return Number(row.retrieved ?? row.recalled ?? 0) === 1;
}

type EventDeviceRetrievalFields = {
  deviceId?: unknown;
  retrieved?: number;
  recalled?: number;
};

/** Distinct rear device IDs on an event/race that are not yet marked retrieved. */
export function filterUnretrievedEventDeviceIds(devices: EventDeviceRetrievalFields[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const row of devices) {
    const id = String(row.deviceId ?? '').trim();
    if (!id || seen.has(id) || !isRearDeviceId(id)) continue;
    if (isEventDeviceRetrieved(row)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/** Distinct assigned device IDs on a race (rear and front rows each count). */
export function countAssignedRaceDevices(rows: EventDeviceRow[]): number {
  const seen = new Set<string>();
  for (const row of rows) {
    const id = String(row.deviceId ?? '').trim();
    if (id) seen.add(id);
  }
  return seen.size;
}

export function countRetrievedRaceDevices(rows: EventDeviceRow[]): number {
  let count = 0;
  for (const row of rows) {
    const id = String(row.deviceId ?? '').trim();
    if (!id) continue;
    if (isEventDeviceRetrieved(row)) count += 1;
  }
  return count;
}

/** Retrieval tracking and UI apply from Distribution onward. */
export function raceStatusShowsRetrieval(raceStatus: string | undefined): boolean {
  const st = normalizeRaceStatus(raceStatus ?? '');
  return st === 'DISTRIBUTION' || st === 'RACKING' || st === 'ACTIVE' || st === 'REPORTING' || st === 'COMPLETE';
}

/**
 * Devices still to retrieve for this race (Distribution → Active).
 * Decreases as devices are marked retrieved in Distribution, Racking, and Active.
 */
export function computeDevicesPendingRetrieval(
  rows: EventDeviceRow[],
  raceStatus: string | undefined,
): number | null {
  const st = normalizeRaceStatus(raceStatus ?? '');
  if (st === 'DRAFT' || st === 'DELETED' || st === 'COMPLETE' || st === 'PACKAGING') return null;
  if (raceStatusShowsRetrieval(raceStatus)) {
    const total = countAssignedRaceDevices(rows);
    const retrieved = countRetrievedRaceDevices(rows);
    return Math.max(0, total - retrieved);
  }
  return null;
}

/** Distinct athletes with at least one assigned device on this race. */
export function countRaceAthleteDevicePairs(rows: EventDeviceRow[]): number {
  const seen = new Set<string>();
  for (const row of rows) {
    const athleteId = String(row.assignedAthleteId ?? '').trim();
    const deviceId = String(row.deviceId ?? '').trim();
    if (!athleteId || !deviceId) continue;
    seen.add(athleteId);
  }
  return seen.size;
}

/** Distinct athletes with BOTH a rear and a front device assigned — a fully confirmed pairing. */
export function countCompleteRaceAthleteDevicePairs(rows: EventDeviceRow[]): number {
  const rearByAthlete = new Set<string>();
  const frontByAthlete = new Set<string>();
  for (const row of rows) {
    const athleteId = String(row.assignedAthleteId ?? '').trim();
    const deviceId = String(row.deviceId ?? '').trim();
    if (!athleteId || !deviceId) continue;
    if (isRearDeviceId(deviceId)) rearByAthlete.add(athleteId);
    else if (isFrontDeviceId(deviceId)) frontByAthlete.add(athleteId);
  }
  let complete = 0;
  for (const athleteId of rearByAthlete) {
    if (frontByAthlete.has(athleteId)) complete += 1;
  }
  return complete;
}

/** Unretrieved device count; null when retrieval tracking does not apply. */
export function countUnretrievedRaceDevices(
  rows: EventDeviceRow[],
  raceStatus: string | undefined,
): number | null {
  return computeDevicesPendingRetrieval(rows, raceStatus);
}

export type RetrievedByPhaseCounts = {
  distribution: number;
  racking: number;
  active: number;
  reporting: number;
  unknown: number;
};

/** Retrieved devices grouped by lifecycle phase (DISTRIBUTION / RACKING / ACTIVE / REPORTING). */
export function countRetrievedRaceDevicesByPhase(rows: EventDeviceRow[]): RetrievedByPhaseCounts {
  const counts: RetrievedByPhaseCounts = {
    distribution: 0,
    racking: 0,
    active: 0,
    reporting: 0,
    unknown: 0,
  };
  for (const row of rows) {
    if (!isEventDeviceRetrieved(row)) continue;
    const phase = String(row.retrievedPhase ?? '').trim().toUpperCase();
    if (phase === 'DISTRIBUTION') counts.distribution += 1;
    else if (phase === 'RACKING') counts.racking += 1;
    else if (phase === 'ACTIVE') counts.active += 1;
    else if (phase === 'REPORTING') counts.reporting += 1;
    else counts.unknown += 1;
  }
  return counts;
}

export type RaceRetrievalProgressSegment = {
  key: string;
  label: string;
  count: number;
  percent: number;
  colorClass: string;
};

const RETRIEVAL_SEGMENT_COLORS: Record<string, string> = {
  distribution: 'bg-sky-500',
  racking: 'bg-violet-500',
  active: 'bg-emerald-500',
  reporting: 'bg-indigo-500',
  unknown: 'bg-slate-400',
  unretrieved: 'bg-amber-400',
};

/** Segments for a 100% stacked progress bar (retrieved phases + unretrieved remainder). */
export function buildRaceRetrievalProgressSegments(
  rows: EventDeviceRow[],
  raceStatus: string | undefined,
): RaceRetrievalProgressSegment[] {
  const total = countAssignedRaceDevices(rows);
  if (total <= 0) return [];

  const byPhase = countRetrievedRaceDevicesByPhase(rows);
  const pending = countUnretrievedRaceDevices(rows, raceStatus) ?? Math.max(0, total - countRetrievedRaceDevices(rows));

  const segments: RaceRetrievalProgressSegment[] = [];
  const push = (key: string, label: string, count: number): void => {
    if (count <= 0) return;
    segments.push({
      key,
      label,
      count,
      percent: (count / total) * 100,
      colorClass: RETRIEVAL_SEGMENT_COLORS[key] ?? 'bg-gray-400',
    });
  };

  push('distribution', 'Distribution', byPhase.distribution);
  push('racking', 'Racking', byPhase.racking);
  push('active', 'Active', byPhase.active);
  push('reporting', 'Reporting', byPhase.reporting);
  push('unknown', 'Retrieved', byPhase.unknown);
  push('unretrieved', 'Unretrieved', pending);

  return segments;
}
